import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { config } from "@/config";
import { apiFetch } from "@/lib/api-client";

// Type definitions for different label responses
export interface PersonaItem {
  id: string;
  persona: string;
  citationRefs?: string[];
}

export type CitationType = 'user' | 'jira' | 'document' | 'figma' | 'confluence' | 'code' | 'prompt';

export interface NodeCitation {
  _id: string | number;
  status: string;
  projectUuid: string;
  name: string;
  type: CitationType;
  reference: string;
}

export interface NodeCitationsResponse {
  data: NodeCitation[];
  total: number;
}

export interface OutcomeItem {
  id: string;
  outcome: string;
  personaId: string;
  citationRefs?: string[];
}

export interface ScenarioItem {
  id: string;
  scenario: string;
  outcomeId: string;
  citationRefs?: string[];
  isDesignGenerated?: boolean;
}

export interface StepItem {
  id: string;
  step: string;
  scenarioId: string;
  preSteps?: string[];
  postSteps?: string[];
  citationRefs?: string[];
}

export interface ActionItem {
  id: string;
  action: string;
  stepId: string;
  citationRefs?: string[];
}

export interface ApiItem {
  id: string;
  url: string;
  method: string;
  actionId: string;
  createdAt: string;
  updatedAt: string;
  projectUuid: string;
  embedding?: number[];
}

export type FunctionalGraphItem =
  | PersonaItem
  | OutcomeItem
  | ScenarioItem
  | StepItem
  | ActionItem
  | ApiItem;

interface FunctionalGraphParams {
  projectUuid: string;
  label: "Persona" | "Outcome" | "Scenario" | "Step" | "Action" | "Api";
  filters?: Record<string, any>;
  limit?: number;
  page?: number;
  sortName?: string;
  sortOrder?: "asc" | "desc";
}

export interface FunctionalGraphResponse {
  data: FunctionalGraphItem[];
  total: number;
  page: number;
}

const getFunctionalGraphData = async ({
  projectUuid,
  label,
  filters,
  pageParam = 1,
  limit = 10,
  headers,
  sortName,
  sortOrder,
}: FunctionalGraphParams & {
  pageParam?: number;
  headers: Record<string, string>;
}): Promise<FunctionalGraphResponse> => {
  let url = `${config.isometricApiUrl}/functional-graph/${projectUuid}/${label}?page=${pageParam}&limit=${limit}`;
  if (sortName && sortOrder) {
    url += `&sortName=${sortName}&sortOrder=${sortOrder}`;
  }
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (typeof value === "object" && value !== null) {
        Object.entries(value).forEach(([operator, operand]) => {
          if (Array.isArray(operand)) {
            // Use `[]` bracket-array notation so a one-element `$in` still
            // parses as an array on the backend (qs would otherwise read a
            // single `filters[k][$in]=v` as a string, and MongoDB `$in`
            // requires an array).
            for (const item of operand) {
              url += `&filters[${key}][${operator}][]=${encodeURIComponent(String(item))}`;
            }
          } else {
            url += `&filters[${key}][${operator}]=${encodeURIComponent(String(operand))}`;
          }
        });
      } else {
        url += `&filters[${key}][$eq]=${encodeURIComponent(String(value))}`;
      }
    });
  }

  const response = await apiFetch(url, {
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch ${label} data: ${response.statusText}`);
  }

  const result: { data: FunctionalGraphItem[]; total: number } =
    await response.json();
  return {
    data: result.data || [],
    total: result.total || 0,
    page: pageParam,
  };
};

export const useGetFunctionalGraphService = ({
  projectUuid,
  label,
  filters,
  limit = 10,
  page = 1,
  enabled,
}: FunctionalGraphParams & { enabled: boolean }) => {
  const { getAuthHeaders } = useAuthHeaders();

  return useQuery({
    queryKey: ["functional-graph", projectUuid, label, page, limit, filters],
    queryFn: () =>
      getFunctionalGraphData({
        projectUuid,
        label,
        filters,
        pageParam: page,
        limit,
        headers: getAuthHeaders(),
        sortName: label === "Api" ? undefined : (label === "Action" || label === "Step") ? "order" : label.toLowerCase(),
        sortOrder: label === "Api" ? undefined : "asc",
      }),
    enabled: enabled && !!projectUuid,
    staleTime: 0, // Always refetch on mount/invalidation
  });
};

export const useGetFunctionalGraphService2 = ({
  projectUuid,
  label,
  filters,
  limit = 10,
  enabled,
}: FunctionalGraphParams & { enabled: boolean }) => {
  const { getAuthHeaders } = useAuthHeaders();

  return useInfiniteQuery({
    queryKey: ["functional-graph", projectUuid, label, filters],
    queryFn: ({ pageParam }) =>
      getFunctionalGraphData({
        projectUuid,
        label,
        filters,
        pageParam,
        limit,
        headers: getAuthHeaders(),
      }),
    getNextPageParam: (lastPage) => {
      const loadedCount = lastPage.page * limit;
      if (loadedCount < lastPage.total) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: enabled && !!projectUuid,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
